import { apiClient } from './api-client';
import { setTokens } from '@/lib/tokenStorage';
import type { AuthResponse } from '@centraoneplus/shared-types';
import { sendWithEncryptedPasswords } from '@/lib/credentialEncryption';

export interface SetupTokenValidation {
  valid: boolean;
  email?: string;
  tenantName?: string;
  tenantId?: string;
  role?: string;
  firstName?: string;
  lastName?: string;
  expiresAt?: string;
  error?: string;
}

export interface CompleteSetupDto {
  token: string;
  password: string;
  firstName?: string;
  lastName?: string;
}

class SetupService {
  private readonly basePath = '/auth';

  /**
   * Validate a setup token.
   * This is called when the user lands on the setup page.
   */
  async validateToken(token: string): Promise<SetupTokenValidation> {
    // POST with the token in the body: a URL path ends up in access logs
    // (VAPT, bug #20941).
    try {
      const response = await apiClient.post<SetupTokenValidation>(
        `${this.basePath}/validate-setup-token`,
        { token }
      );
      return response.data;
    } catch (err) {
      // A BFF released before the POST existed answers 404. Fall back until
      // the new BFF is everywhere, then remove.
      if ((err as { response?: { status?: number } })?.response?.status !== 404) throw err;
      const response = await apiClient.get<SetupTokenValidation>(
        `${this.basePath}/validate-setup-token/${encodeURIComponent(token)}`
      );
      return response.data;
    }
  }

  /**
   * Complete the account setup.
   * Creates the user account and returns auth tokens.
   */
  async completeSetup(dto: CompleteSetupDto): Promise<AuthResponse> {
    const response = await sendWithEncryptedPasswords(dto, (body) =>
      apiClient.post<AuthResponse>(`${this.basePath}/complete-setup`, body)
    );

    // Store tokens; the new user stays signed in across tab closes/restarts.
    const { accessToken, refreshToken } = response.data;
    setTokens(accessToken, refreshToken);

    return response.data;
  }
}

export const setupService = new SetupService();
